/**
 * SuggestedThemeChips — clickable chips derived from Industry Research's
 * executive summary and disruptions. Each chip seeds a new manual question
 * pre-populated with the theme text.
 *
 * Rendered above the "Generate Questions" button in Step 4. Hidden entirely
 * if no insights data exists (chips are seed-helpers, not core flow).
 *
 * Sources: opportunityAreas, riskAreas, strategicImperatives,
 *          activeDisruptions[].name
 *
 * Adds questions through the existing setQuestionsData path so no new
 * store mutation pattern is introduced.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import type { QuestionData } from '../../stores/wizardStore';

interface SuggestedThemeChipsProps {
  industryId: string | null | undefined;
  onAdd: (q: QuestionData) => void;
}

type ChipTone = 'opportunity' | 'risk' | 'imperative' | 'disruption';

interface Chip {
  label: string;
  tone: ChipTone;
  category: string;
  priority: 'Critical' | 'High' | 'Medium';
}

interface IndustryInsightsResponse {
  industryInsights?: {
    executiveSummary?: {
      opportunityAreas?: string[];
      riskAreas?: string[];
      strategicImperatives?: string[];
    } | null;
    disruptions?: {
      activeDisruptions?: any[];
    } | null;
  } | null;
}

function buildChips(data: IndustryInsightsResponse | undefined): Chip[] {
  if (!data?.industryInsights) return [];
  const exec = data.industryInsights.executiveSummary ?? {};
  const disr = data.industryInsights.disruptions ?? {};
  const chips: Chip[] = [];

  for (const t of exec.opportunityAreas ?? []) {
    chips.push({ label: t, tone: 'opportunity', category: 'Opportunity', priority: 'High' });
  }
  for (const t of exec.riskAreas ?? []) {
    chips.push({ label: t, tone: 'risk', category: 'Risk', priority: 'High' });
  }
  for (const t of exec.strategicImperatives ?? []) {
    chips.push({ label: t, tone: 'imperative', category: 'Strategy', priority: 'Critical' });
  }
  for (const d of disr.activeDisruptions ?? []) {
    const name = (d && (d.name || d.title)) || (typeof d === 'string' ? d : null);
    if (name) {
      chips.push({ label: name, tone: 'disruption', category: 'Disruption', priority: 'Critical' });
    }
  }
  return chips;
}

const toneClasses: Record<ChipTone, string> = {
  opportunity: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
  risk: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
  imperative: 'bg-primary-50 text-primary-700 border-primary-200 hover:bg-primary-100',
  disruption: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
};

const SuggestedThemeChips: React.FC<SuggestedThemeChipsProps> = ({ industryId, onAdd }) => {
  const { data } = useQuery<IndustryInsightsResponse>({
    queryKey: ['case-context', 'industry-insights', industryId],
    queryFn: () => api.get(`/case-discovery/industry-insights/${industryId}`),
    enabled: !!industryId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const chips = React.useMemo(() => buildChips(data), [data]);
  if (!industryId || chips.length === 0) return null;

  const handleClick = (chip: Chip) => {
    const question: QuestionData = {
      questionId: `seed_q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      personaRole: 'CEO',
      question: `How does ${chip.label} affect our strategic direction over the next 12–24 months?`,
      context: `Seeded from Industry Research ${chip.tone}.`,
      category: chip.category,
      priority: chip.priority,
      selected: true,
    };
    onAdd(question);
  };

  return (
    <div className="p-4 mb-4 border border-dashed border-neutral-300 rounded-lg bg-neutral-50">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-primary-600" />
        <p className="text-sm font-medium text-neutral-700">
          Suggested themes from Industry Research
        </p>
        <span className="text-xs text-neutral-500">click a theme to seed a question</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {chips.map((chip, idx) => (
          <button
            key={`${chip.tone}-${idx}`}
            onClick={() => handleClick(chip)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border transition-colors ${toneClasses[chip.tone]}`}
          >
            <Plus className="w-3 h-3" />
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SuggestedThemeChips;
